import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

const CategoriesSection = styled.div`
  margin: 0 0 0.5rem;
  padding: 0.25rem 0;
  background: transparent;
  border: none;
  position: relative;
  width: 100%;
`;

const CategoriesCarouselContainer = styled.div`
  position: relative;
  padding: 0 2.5rem;

  @media (max-width: 768px) {
    padding: 0 2.25rem;
  }
`;

const CategoriesCarousel = styled.div`
  display: flex;
  gap: 0.6rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  padding: 0.15rem 0 0.35rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  font-size: 1.35rem;
  line-height: 1;
  color: var(--text-primary, #1f2937);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: opacity 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  &.left {
    left: 0;
  }
  &.right {
    right: 0;
  }
`;

const cardActiveStyles = {
  default: css`
    background: rgba(102, 126, 234, 0.12);
    border-color: var(--primary-color, #667eea);
  `,
  purple: css`
    background: rgba(118, 75, 162, 0.14);
    border-color: #764ba2;
  `,
  pink: css`
    background: rgba(190, 24, 93, 0.12);
    border-color: #be185d;
  `,
  boutique: css`
    background: rgba(19, 78, 74, 0.1);
    border-color: #0f766e;
  `,
};

const CategoryCard = styled.button`
  background: transparent;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  flex-shrink: 0;
  min-width: 76px;
  scroll-snap-align: start;

  &:focus-visible {
    outline: none;
    border-color: var(--primary-color, #667eea);
  }

  &.active {
    ${(p) => cardActiveStyles[p.$variant] || cardActiveStyles.default}
  }

  @media (max-width: 768px) {
    min-width: 72px;
    padding: 0.45rem 0.45rem;
  }
`;

const CategoryIcon = styled.div`
  font-size: 1.35rem;
  line-height: 1.2;
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CategoryName = styled.span`
  font-weight: 600;
  color: var(--text-primary, #1f2937);
  font-size: 0.8rem;
  text-align: center;
  white-space: nowrap;
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

/**
 * Shared category carousel — frameless strip with always-visible left/right arrows.
 */
const CategoryCarousel = ({
  categories,
  selectedCategory = '',
  onSelectCategory,
  variant = 'default',
  toggleOnReselect = false,
}) => {
  const stripRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [categories.length, updateScrollState]);

  const scrollByDir = (direction) => {
    const el = stripRef.current;
    if (!el) return;
    const amount = Math.max(180, Math.floor(el.clientWidth * 0.65));
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const handleSelect = (value) => {
    if (toggleOnReselect && selectedCategory === value) {
      onSelectCategory('');
      return;
    }
    onSelectCategory(value);
  };

  return (
    <CategoriesSection>
      <CategoriesCarouselContainer>
        <CarouselButton
          type="button"
          className="left"
          onClick={() => scrollByDir('left')}
          disabled={!canScrollLeft}
          aria-label="Scroll categories left"
        >
          ‹
        </CarouselButton>
        <CategoriesCarousel ref={stripRef}>
          {categories.map((cat) => (
            <CategoryCard
              key={cat.value || 'all'}
              type="button"
              $variant={variant}
              className={selectedCategory === cat.value ? 'active' : ''}
              onClick={() => handleSelect(cat.value)}
              aria-pressed={selectedCategory === cat.value}
            >
              <CategoryIcon aria-hidden>{cat.icon}</CategoryIcon>
              <CategoryName>{cat.name}</CategoryName>
            </CategoryCard>
          ))}
        </CategoriesCarousel>
        <CarouselButton
          type="button"
          className="right"
          onClick={() => scrollByDir('right')}
          disabled={!canScrollRight}
          aria-label="Scroll categories right"
        >
          ›
        </CarouselButton>
      </CategoriesCarouselContainer>
    </CategoriesSection>
  );
};

export default CategoryCarousel;
